import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { formatStatFraction } from './packagingSupplierStats'

export type ProgressSegment = {
  key: string
  count: number
  className: string
  label: string
  tooltip: string
  onSelect?: () => void
}

type SegmentedProgressBarProps = {
  segments: ProgressSegment[]
  total: number
}

function segmentWidth(count: number, total: number): string {
  if (total <= 0 || count <= 0) {
    return '0%'
  }
  return `${(count / total) * 100}%`
}

export function SegmentedProgressBar({
  segments,
  total,
}: SegmentedProgressBarProps) {
  return (
    <div className='space-y-3 rounded-lg border p-4'>
      <div
        className='flex h-3 w-full overflow-hidden rounded-full bg-muted'
        role='img'
        aria-label={segments.map((segment) => segment.label).join(', ')}
      >
        {total > 0
          ? segments.map((segment) => {
              if (segment.count <= 0) {
                return null
              }
              return (
                <div
                  key={segment.key}
                  className={segment.className}
                  style={{ width: segmentWidth(segment.count, total) }}
                  title={`${segment.label}: ${formatStatFraction(segment.count, total)}`}
                />
              )
            })
          : null}
      </div>
      <div className='flex flex-wrap gap-x-6 gap-y-2 text-sm'>
        {segments.map((segment) => {
          const content = (
            <>
              <span
                className={`size-2.5 shrink-0 rounded-full ${segment.className}`}
                aria-hidden
              />
              <span className='text-muted-foreground'>{segment.label}</span>
              <span className='font-medium tabular-nums'>
                {formatStatFraction(segment.count, total)}
              </span>
            </>
          )
          return (
            <Tooltip key={segment.key}>
              <TooltipTrigger asChild>
                {segment.onSelect != null ? (
                  <button
                    type='button'
                    className='inline-flex items-center gap-2'
                    onClick={segment.onSelect}
                  >
                    {content}
                  </button>
                ) : (
                  <span className='inline-flex cursor-default items-center gap-2'>
                    {content}
                  </span>
                )}
              </TooltipTrigger>
              <TooltipContent side='bottom' className='max-w-xs'>
                {segment.tooltip}
              </TooltipContent>
            </Tooltip>
          )
        })}
      </div>
    </div>
  )
}
